import React, { useEffect, useState } from "react";
import { ReactComponent as NewDesignIcon } from "../../../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../../../assets/svg/NewRenovationIcon.svg";
import { milestoneStatusStyleArray } from "../../../../../../pages/NewProjectDetails/config";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import moment from "moment";
import { Setting } from "../../../../../../utils/Setting";
import { getApiData } from "../../../../../../utils/APIHelper";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  milestoneCrTablePaperSx,
  milestoneCrHeadCellSx,
  milestoneCrCheckboxSx,
  getDatePillSx,
  milestoneCrStatusDotSx,
} from "../shared/milestoneCrTableStyles";

const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const TimelineMilestoneCR = ({ data }) => {
  const { id } = useParams();
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);

  const changeRequest = data;

  const snapshot = React.useMemo(() => {
    try {
      return changeRequest?.snapshot ? JSON.parse(changeRequest.snapshot) : {};
    } catch (e) {
      return {};
    }
  }, [changeRequest?.snapshot]);

  const snapshotMilestones = snapshot?.milestones || [];

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const res = await getApiData(`${Setting.endpoints.getProject}?proposal_id=${id}`, "GET", {}, {}, false);
      if (res?.success && res?.data?.length > 0) {
        setMilestones(res.data[0].milestone || []);
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch milestones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (changeRequest?.projectId) {
      fetchProjectDetails();
    }
  }, [changeRequest?.projectId]);

  const isMilestoneChecked = (milestoneId) => {
    if (!snapshotMilestones || snapshotMilestones.length === 0) return false;
    return snapshotMilestones.some((sm) => sm.id === milestoneId);
  };

  const getSnapshotMilestone = (milestoneId) => {
    return snapshotMilestones.find((sm) => sm.id === milestoneId);
  };

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Typography sx={{ fontSize: "14px", color: "#6b7280", mb: 3, fontWeight: 400 }}>
        Select milestones to include them in this change request.
      </Typography>

      <TableContainer component={Paper} elevation={0} sx={milestoneCrTablePaperSx}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 52, ...milestoneCrHeadCellSx }} />
              <TableCell sx={milestoneCrHeadCellSx}>Milestone Name</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Start Date</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>End Date</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Tags</TableCell>
              <TableCell sx={milestoneCrHeadCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {milestones.map((m) => {
              const checked = isMilestoneChecked(m.id);
              const sm = getSnapshotMilestone(m.id);

              const startDateToShow = sm ? sm.proposedStartDate : m.start_date;
              const endDateToShow = sm ? sm.proposedEndDate : m.end_date;

              const isStartDateChanged = checked && sm?.originalStartDate !== sm?.proposedStartDate;
              const isEndDateChanged = checked && sm?.originalEndDate !== sm?.proposedEndDate;

              const statusBg =
                milestoneStatusStyleArray.find(
                  (style) => style.status?.toLowerCase() === m.status?.toLowerCase()
                )?.backgroundColor || "#fa8c16";

              return (
                <TableRow key={m.id}>
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      disabled
                      disableRipple
                      tabIndex={-1}
                      sx={milestoneCrCheckboxSx}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", fontWeight: 600, color: "#0d0d0e" }}>
                    {m.milestone_name}
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={getDatePillSx(isStartDateChanged)}>
                      {startDateToShow ? moment(startDateToShow).format("DD/MM/YYYY") : "—"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box component="span" sx={getDatePillSx(isEndDateChanged)}>
                      {endDateToShow ? moment(endDateToShow).format("DD/MM/YYYY") : "—"}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap="8px">
                      {m.tags && (
                        <Box display="flex" alignItems="center" sx={{ color: "#949DAA" }}>
                          {m.tags === "Design" ? <NewDesignIcon /> : <NewRenovationIcon />}
                        </Box>
                      )}
                      <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 500 }}>
                        {m.tags || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Box sx={milestoneCrStatusDotSx(statusBg)} />
                      <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 500 }}>
                        {getStatusDisplayText(m.status) || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && milestones.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  No milestones available.
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  Loading milestones…
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TimelineMilestoneCR;
